// ManDana — transport-neutral same-desk collaboration frames.
// The transport sees opaque bytes. This codec is deliberately above that seam
// so sealing, Nostr relays, or another byte carrier can be inserted later.
(function (g: any) {
  'use strict';

  const MD_COLLAB_VERSION = 1;
  const MD_COLLAB_MAX_FRAME_BYTES = 256 * 1024;
  const DESK = /^[a-z0-9]{10}$/;
  const DEVICE = /^[0-9a-f]{16}$/;
  const ACTOR = /^[0-9a-f]{32}$/;
  const HASH = /^[0-9a-f]{64}$/;
  const TYPES = new Set(['presence', 'pointer', 'stroke', 'scene', 'snapshot-request', 'asset', 'ack']);

  const finite = (n: any) => typeof n === 'number' && Number.isFinite(n);
  const integer = (n: any) => Number.isSafeInteger(n) && n >= 0;
  const short = (s: any, max: number) => typeof s === 'string' && s.length <= max;
  const point = (p: any) => Array.isArray(p) && p.length === 2 && finite(p[0]) && finite(p[1]);
  const points = (value: any) => Array.isArray(value) && value.length <= 8192 && value.every(point);
  const pressures = (value: any) => Array.isArray(value) && value.length <= 8192
    && value.every((pressure: any) => finite(pressure) && pressure >= 0 && pressure <= 1);

  function base(raw: any): any {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    if (raw.v !== MD_COLLAB_VERSION || !TYPES.has(raw.t)) return null;
    if (!DESK.test(raw.desk) || !DEVICE.test(raw.dev) || !ACTOR.test(raw.actor)) return null;
    if (!integer(raw.seq) || !finite(raw.sentAt) || raw.sentAt < 0) return null;
    return {
      v: MD_COLLAB_VERSION, t: raw.t, desk: raw.desk, dev: raw.dev,
      actor: raw.actor, seq: raw.seq, sentAt: raw.sentAt,
    };
  }

  function normalise(raw: any): any {
    const out = base(raw);
    if (!out) return null;

    if (raw.t === 'presence') {
      if (!['join', 'heartbeat', 'leave'].includes(raw.phase)) return null;
      if (!short(raw.name, 80)) return null;
      return { ...out, phase: raw.phase, name: raw.name };
    }

    if (raw.t === 'snapshot-request') return out;

    if (raw.t === 'ack') {
      if (!integer(raw.forSeq)) return null;
      return { ...out, forSeq: raw.forSeq };
    }

    if (raw.t === 'pointer') {
      if (!raw.pointer || typeof raw.pointer !== 'object') return null;
      const { x, y } = raw.pointer;
      if (!finite(x) || !finite(y)) return null;
      const button = raw.button === 'down' ? 'down' : 'up';
      const tool = short(raw.tool, 32) ? raw.tool : '';
      const selected = Array.isArray(raw.selected)
        ? raw.selected.filter((id: any) => short(id, 128)).slice(0, 128)
        : [];
      return { ...out, pointer: { x, y }, button, tool, selected };
    }

    if (raw.t === 'stroke') {
      /* A stroke starts with one reliable Excalidraw shell, then carries only
         its growing point/pressure tail in replaceable frames. Every tail is
         cumulative from `from`, so losing yesterday's frame never leaves a
         permanent hole; the next one heals it, and the canonical scene frame
         on pointer-up remains the final authority. */
      if (!['start', 'tail'].includes(raw.phase)) return null;
      if (raw.phase === 'start') {
        const element = raw.element;
        if (!element || typeof element !== 'object' || Array.isArray(element)
          || !short(element.id, 128) || element.type !== 'freedraw'
          || !points(element.points) || !element.points.length
          || !pressures(element.pressures || [])) return null;
        return { ...out, phase: 'start', element };
      }
      if (!short(raw.id, 128) || !integer(raw.from) || !points(raw.points)
        || !pressures(raw.pressures || []) || !finite(raw.x) || !finite(raw.y)
        || !finite(raw.width) || !finite(raw.height) || !integer(raw.version)
        || !integer(raw.versionNonce) || (raw.lastCommittedPoint !== null
          && !point(raw.lastCommittedPoint))) return null;
      return {
        ...out, phase: 'tail', id: raw.id, from: raw.from,
        points: raw.points, pressures: raw.pressures || [],
        x: raw.x, y: raw.y, width: raw.width, height: raw.height,
        version: raw.version, versionNonce: raw.versionNonce,
        lastCommittedPoint: raw.lastCommittedPoint,
      };
    }

    if (raw.t === 'asset') {
      /* The reference a board file already is on this build — {blob:1, hash,
         size} — so an asset frame announces the same name the sync layer and
         the blob store agree on, not a parallel spelling of it. */
      if (!short(raw.fileId, 128) || !HASH.test(raw.hash)) return null;
      if (!short(raw.mimeType, 128) || !integer(raw.size)) return null;
      return {
        ...out, fileId: raw.fileId, hash: raw.hash,
        mimeType: raw.mimeType, size: raw.size,
      };
    }

    if (!['progress', 'final', 'snapshot'].includes(raw.phase)) return null;
    if (!Array.isArray(raw.elements) || raw.elements.length > 256) return null;
    /* Elements remain Excalidraw-owned values. The byte limit bounds their
       aggregate size; per-element object checks stop primitive-shaped frames. */
    if (raw.elements.some((el: any) => !el || typeof el !== 'object' || Array.isArray(el)
      || !short(el.id, 128))) return null;
    const part = integer(raw.part) ? raw.part : 0;
    const total = integer(raw.total) && raw.total > 0 ? raw.total : 1;
    if (part >= total || total > 4096) return null;
    return { ...out, phase: raw.phase, part, total, elements: raw.elements };
  }

  function mdCollabEncode(frame: any): Uint8Array | null {
    const ok = normalise(frame);
    if (!ok || typeof g.TextEncoder !== 'function') return null;
    let bytes: Uint8Array;
    try { bytes = new g.TextEncoder().encode(JSON.stringify(ok)); }
    catch { return null; }
    return bytes.byteLength > 0 && bytes.byteLength <= MD_COLLAB_MAX_FRAME_BYTES ? bytes : null;
  }

  function mdCollabDecode(bytes: any): any {
    if (!(bytes instanceof Uint8Array) || !bytes.byteLength
      || bytes.byteLength > MD_COLLAB_MAX_FRAME_BYTES || typeof g.TextDecoder !== 'function') return null;
    try {
      const raw = JSON.parse(new g.TextDecoder('utf-8', { fatal: true }).decode(bytes));
      return normalise(raw);
    } catch { return null; }
  }

  Object.assign(g, {
    MD_COLLAB_VERSION, MD_COLLAB_MAX_FRAME_BYTES, mdCollabEncode, mdCollabDecode,
  });
})(typeof window !== 'undefined' ? window : globalThis);
